import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import { Badge, Button, Card, Input } from "../../components/ui-kit";
import { SlidersHorizontal, X } from "@phosphor-icons/react";

const ROLE_OPTIONS = ["customer", "driver", "courier", "merchant", "agent", "admin"];
const ACCESS_MODULES = [
  { key: "customer", label: "Client" },
  { key: "driver", label: "Chauffeur" },
  { key: "courier", label: "Livreur" },
  { key: "merchant", label: "Marchand" },
  { key: "agent", label: "Agent" },
  { key: "admin", label: "Admin" },
  { key: "finance", label: "Argent" },
  { key: "support", label: "Support" },
  { key: "marketplace", label: "Marketplace" },
  { key: "delivery_ops", label: "Livraisons" },
  { key: "driver_onboarding", label: "Validation chauffeurs" },
  { key: "merchant_onboarding", label: "Validation partenaires" },
  { key: "ads", label: "Publicites" },
  { key: "settings", label: "Parametres" },
];
const AGENT_PERMISSION_KEYS = new Set(["support", "merchant_onboarding", "driver_onboarding", "delivery_ops", "finance"]);
const ROLE_PRIORITY = ["admin", "agent", "merchant", "driver", "courier", "customer"];
const selectClass = "w-full rounded-xl border border-white/10 bg-[#15171b] px-3 py-2 text-xs font-semibold text-white outline-none [color-scheme:dark]";
const compactButtonClass = "min-h-8 px-3 py-1.5 text-xs";

function roleTone(role) {
  if (role === "admin") return "primary";
  if (role === "driver" || role === "courier") return "warning";
  if (role === "merchant") return "success";
  if (role === "agent") return "primary";
  return "default";
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [cities, setCities] = useState([]);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [accessFilter, setAccessFilter] = useState("all");
  const [detailUser, setDetailUser] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => {
    api
      .get("/admin/users")
      .then(({ data }) => setUsers(Array.isArray(data) ? data : []))
      .catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/admin/cities").then(({ data }) => setCities(Array.isArray(data) ? data : [])).catch(() => setCities([]));
  }, []);

  const cityName = (cityId) => {
    const city = cities.find((c) => c.id === cityId);
    return city ? `${city.name} - ${city.province}` : "Ville non definie";
  };

  const modulesFor = (u) => {
    const modules = new Set(Array.isArray(u.access_modules) ? u.access_modules : []);
    if (u.role) modules.add(u.role);
    return Array.from(modules);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      const modules = new Set(modulesFor(u));
      const matchesQuery = !q || [u.name, u.email, u.phone, u.role, u.account_status]
        .some((v) => String(v || "").toLowerCase().includes(q));
      const matchesRole = roleFilter === "all" || u.role === roleFilter;
      const matchesCity = cityFilter === "all" || (cityFilter === "none" ? !u.city_id : u.city_id === cityFilter);
      const matchesStatus = statusFilter === "all" || (statusFilter === "blocked" ? u.account_status === "BLOCKED" : u.account_status !== "BLOCKED");
      const matchesAccess = accessFilter === "all" || modules.has(accessFilter);
      return matchesQuery && matchesRole && matchesCity && matchesStatus && matchesAccess;
    });
  }, [users, query, roleFilter, cityFilter, statusFilter, accessFilter]);

  const patchUser = async (id, payload, label = "Utilisateur mis a jour") => {
    setBusy(`${id}:${Object.keys(payload)[0]}`);
    try {
      await api.patch(`/admin/users/${id}`, payload);
      toast.success(label);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const toggleModule = (u, moduleKey) => {
    const modules = new Set(modulesFor(u));
    if (modules.has(moduleKey)) {
      modules.delete(moduleKey);
    } else {
      modules.add(moduleKey);
    }
    if (!modules.size) modules.add("customer");
    const access_modules = Array.from(modules);
    const role = ROLE_PRIORITY.find((item) => access_modules.includes(item)) || u.role || "customer";
    const agent_permissions = access_modules.filter((item) => AGENT_PERMISSION_KEYS.has(item));
    patchUser(
      u.id,
      {
        role,
        access_modules,
        agent_permissions: agent_permissions.length ? agent_permissions : undefined,
      },
      "Acces mis a jour",
    );
  };

  const setPrimaryRole = (u, role) => {
    const modules = new Set(modulesFor(u));
    modules.add(role);
    if (role === "agent") modules.add("support");
    if (role === "merchant") modules.add("marketplace");
    if (role === "courier") modules.add("delivery_ops");
    const access_modules = Array.from(modules);
    const agent_permissions = access_modules.filter((item) => AGENT_PERMISSION_KEYS.has(item));
    patchUser(
      u.id,
      {
        role,
        access_modules,
        agent_permissions: agent_permissions.length ? agent_permissions : undefined,
      },
      "Role principal modifie",
    );
  };

  const AccessCheckboxes = ({ u, compact = false }) => {
    const modules = new Set(modulesFor(u));
    return (
      <div className={compact ? "grid grid-cols-2 gap-2 sm:grid-cols-3" : "grid grid-cols-2 gap-2 xl:grid-cols-3"}>
        {ACCESS_MODULES.map((item) => {
          const checked = modules.has(item.key);
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => toggleModule(u, item.key)}
              disabled={busy.startsWith(u.id)}
              className={`flex min-h-9 items-center gap-2 rounded-xl border px-2.5 py-2 text-left text-[10px] font-black uppercase tracking-[0.12em] transition ${
                checked
                  ? "border-primary/70 bg-primary/15 text-primary shadow-[0_0_24px_rgba(0,212,255,0.14)]"
                  : "border-white/10 bg-white/[0.04] text-muted-foreground hover:border-white/25 hover:text-white"
              }`}
            >
              <span className={`grid h-4 w-4 shrink-0 place-items-center rounded border text-transparent ${checked ? "border-primary bg-primary" : "border-white/25 bg-black/20"}`}>
                {checked && <span className="h-2 w-2 rounded-sm bg-black" />}
              </span>
              <span className="min-w-0 leading-tight">{item.label}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const AccessSummary = ({ u, withButton = true }) => {
    const modules = modulesFor(u);
    const visible = modules.slice(0, 3);
    return (
      <div className="flex min-w-[210px] flex-wrap items-center gap-1.5">
        <Badge tone={roleTone(u.role)}>{u.role}</Badge>
        {visible.map((module) => (
          <Badge key={`${u.id}-${module}`} tone="dark">
            {ACCESS_MODULES.find((item) => item.key === module)?.label || module}
          </Badge>
        ))}
        {modules.length > visible.length && <Badge tone="default">+{modules.length - visible.length}</Badge>}
        {withButton && (
          <Button
            variant="outline"
            className={`${compactButtonClass} ml-1`}
            onClick={() => setDetailUser(u)}
            disabled={busy.startsWith(u.id)}
          >
            <SlidersHorizontal size={15} weight="bold" /> Details
          </Button>
        )}
      </div>
    );
  };

  const UserDetailsModal = () => {
    if (!detailUser) return null;
    const u = users.find((item) => item.id === detailUser.id) || detailUser;
    return (
      <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
        <Card className="max-h-[90dvh] w-full max-w-3xl overflow-y-auto p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-widest text-primary">Details acces</div>
              <h2 className="truncate font-heading text-2xl font-black">{u.name}</h2>
              <p className="truncate text-xs text-muted-foreground">{u.email} - {u.phone}</p>
            </div>
            <button
              type="button"
              onClick={() => setDetailUser(null)}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-white/10 bg-white/5"
            >
              <X size={18} weight="bold" />
            </button>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div>
              <div className="mb-1 text-[10px] font-black uppercase tracking-widest text-muted-foreground">Role principal</div>
              <select
                value={u.role}
                onChange={(e) => setPrimaryRole(u, e.target.value)}
                className={selectClass}
                disabled={busy.startsWith(u.id)}
              >
                {ROLE_OPTIONS.map((role) => (
                  <option className="bg-[#15171b] text-white" key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="mb-1 text-[10px] font-black uppercase tracking-widest text-muted-foreground">Ville</div>
              <select
                value={u.city_id || ""}
                onChange={(e) => patchUser(u.id, { city_id: e.target.value }, "Ville modifiee et email envoye")}
                className={selectClass}
                disabled={busy.startsWith(u.id)}
              >
                <option value="" disabled>Choisir une ville</option>
                {cities.map((city) => (
                  <option className="bg-[#15171b] text-white" key={city.id} value={city.id}>{city.name} - {city.province}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 p-3">
            <div className="mb-3 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
              Modules autorises
            </div>
            <AccessCheckboxes u={u} compact />
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-5">
            <Button className={compactButtonClass} variant="outline" onClick={() => patchUser(u.id, { email_verified: !u.email_verified }, "Verification email modifiee")} disabled={busy.startsWith(u.id)}>
              Email {u.email_verified ? "OK" : "NON"}
            </Button>
            <Button className={compactButtonClass} variant="outline" onClick={() => patchUser(u.id, { phone_verified: !u.phone_verified }, "Verification telephone modifiee")} disabled={busy.startsWith(u.id)}>
              Tel {u.phone_verified ? "OK" : "NON"}
            </Button>
            <Button className={compactButtonClass} variant="outline" onClick={() => resetPassword(u)} disabled={busy.startsWith(u.id)}>Reset</Button>
            <Button className={compactButtonClass} variant={u.account_status === "BLOCKED" ? "secondary" : "danger"} onClick={() => patchUser(u.id, { account_status: u.account_status === "BLOCKED" ? "ACTIVE" : "BLOCKED" }, u.account_status === "BLOCKED" ? "Utilisateur reactive" : "Utilisateur bloque")} disabled={busy.startsWith(u.id)}>
              {u.account_status === "BLOCKED" ? "Reactiver" : "Bloquer"}
            </Button>
            <Button className={compactButtonClass} variant="danger" onClick={() => deleteUser(u)} disabled={busy.startsWith(u.id)}>
              Supprimer
            </Button>
          </div>
        </Card>
      </div>
    );
  };

  const resetPassword = async (u) => {
    const custom = window.prompt(`Nouveau mot de passe pour ${u.name}. Laissez vide pour generer automatiquement.`);
    if (custom === null) return;
    setBusy(`${u.id}:reset`);
    try {
      const payload = { send_email: true };
      if (custom.trim()) payload.new_password = custom.trim();
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`, payload);
      toast.success(`Mot de passe temporaire: ${data.temporary_password}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const deleteUser = async (u) => {
    const ok = window.confirm(`Supprimer definitivement ${u.name} ? Cette action est irreversible si aucune course active n'existe.`);
    if (!ok) return;
    setBusy(`${u.id}:delete`);
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Utilisateur supprime");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const UserCard = ({ u }) => {
    const blocked = u.account_status === "BLOCKED";
    return (
      <Card className="p-4" data-testid={`user-row-mobile-${u.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate font-heading text-lg font-bold">{u.name}</div>
            <div className="truncate font-mono text-xs text-muted-foreground">{u.email}</div>
            <div className="mt-1 font-mono text-xs text-muted-foreground">{u.phone}</div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <Badge tone={roleTone(u.role)}>{u.role}</Badge>
            <Badge tone={blocked ? "danger" : "success"}>{blocked ? "bloque" : "actif"}</Badge>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="col-span-2 rounded-2xl border border-white/10 bg-white/5 p-2">
            <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Ville: {cityName(u.city_id)}
            </div>
            <select
              value={u.city_id || ""}
              onChange={(e) => patchUser(u.id, { city_id: e.target.value }, "Ville modifiee et email envoye")}
              className={selectClass}
              disabled={busy.startsWith(u.id)}
            >
              <option value="" disabled>Choisir une ville</option>
              {cities.map((city) => (
                <option className="bg-[#15171b] text-white" key={city.id} value={city.id}>{city.name} - {city.province}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2 rounded-2xl border border-white/10 bg-black/20 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Acces</span>
              <Button className={compactButtonClass} variant="outline" onClick={() => setDetailUser(u)}>Details</Button>
            </div>
            <AccessSummary u={u} withButton={false} />
          </div>
        </div>
      </Card>
    );
  };

  return (
    <AdminShell title="Utilisateurs">
      <div className="admin-page-header">
        <div className="min-w-0">
          <h1 className="font-heading text-3xl font-black tracking-tighter sm:text-4xl">Utilisateurs</h1>
          <p className="text-sm text-muted-foreground">Modifier, bloquer, reinitialiser ou supprimer les comptes.</p>
        </div>
        <div className="admin-actions w-full max-w-5xl">
          <Input placeholder="Rechercher..." value={query} onChange={(e) => setQuery(e.target.value)} />
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className={`${selectClass} min-w-[140px]`}>
            <option value="all">Tous roles</option>
            {ROLE_OPTIONS.map((role) => <option key={role} value={role}>{role}</option>)}
          </select>
          <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} className={`${selectClass} min-w-[160px]`}>
            <option value="all">Toutes villes</option>
            <option value="none">Sans ville</option>
            {cities.map((city) => <option key={city.id} value={city.id}>{city.name}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`${selectClass} min-w-[130px]`}>
            <option value="all">Tous etats</option>
            <option value="active">Actifs</option>
            <option value="blocked">Bloques</option>
          </select>
          <select value={accessFilter} onChange={(e) => setAccessFilter(e.target.value)} className={`${selectClass} min-w-[170px]`}>
            <option value="all">Tous acces</option>
            {ACCESS_MODULES.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-5 space-y-3 md:hidden">
        {filtered.map((u) => <UserCard key={u.id} u={u} />)}
      </div>

      <Card className="mt-6 hidden overflow-x-auto md:block">
        <table className="min-w-[1120px] w-full text-sm">
          <thead className="border-b-2 border-foreground bg-secondary text-secondary-foreground">
            <tr>
              <th className="p-3 text-left text-[10px] font-black uppercase tracking-widest">Utilisateur</th>
              <th className="p-3 text-left text-[10px] font-black uppercase tracking-widest">Contact</th>
              <th className="p-3 text-left text-[10px] font-black uppercase tracking-widest">Ville</th>
              <th className="p-3 text-left text-[10px] font-black uppercase tracking-widest">Acces</th>
              <th className="p-3 text-left text-[10px] font-black uppercase tracking-widest">Etat</th>
                  <th className="p-3 text-left text-[10px] font-black uppercase tracking-widest">Details</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const blocked = u.account_status === "BLOCKED";
              return (
                <tr key={u.id} className="border-b border-border align-middle" data-testid={`user-row-${u.id}`}>
                  <td className="p-3">
                    <div className="font-bold">{u.name}</div>
                    <div className="text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString("fr-FR")}</div>
                  </td>
                  <td className="p-3">
                    <div className="font-mono text-xs">{u.email}</div>
                    <div className="font-mono text-xs">{u.phone}</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      <Badge tone={u.email_verified ? "success" : "warning"}>email {u.email_verified ? "ok" : "non"}</Badge>
                      <Badge tone={u.phone_verified ? "success" : "warning"}>tel {u.phone_verified ? "ok" : "non"}</Badge>
                    </div>
                  </td>
                  <td className="p-3">
                    <select
                      value={u.city_id || ""}
                      onChange={(e) => patchUser(u.id, { city_id: e.target.value }, "Ville modifiee et email envoye")}
                      className={`${selectClass} min-w-[220px]`}
                      disabled={busy.startsWith(u.id)}
                      data-testid={`admin-user-city-${u.id}`}
                    >
                      <option value="" disabled>Choisir une ville</option>
                      {cities.map((city) => (
                        <option className="bg-[#15171b] text-white" key={city.id} value={city.id}>{city.name} - {city.province}</option>
                      ))}
                    </select>
                    <div className="mt-1 text-[10px] text-muted-foreground">
                      {u.city_verification_method ? `Confirme: ${u.city_verification_method}` : "Non confirme"}
                    </div>
                  </td>
                  <td className="p-3">
                    <AccessSummary u={u} />
                  </td>
                  <td className="p-3"><Badge tone={blocked ? "danger" : "success"}>{blocked ? "bloque" : "actif"}</Badge></td>
                  <td className="p-3">
                    <Button className={compactButtonClass} variant="outline" onClick={() => setDetailUser(u)} disabled={busy.startsWith(u.id)}>
                      <SlidersHorizontal size={15} weight="bold" /> Details
                    </Button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="p-8 text-center text-xs font-bold uppercase tracking-widest text-muted-foreground">Aucun utilisateur</td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
      <UserDetailsModal />
    </AdminShell>
  );
}
